import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import * as Cesium from 'cesium';
import { X } from 'lucide-react';
import { useDataCoverageOverlay } from '../../hooks/useDataCoverageOverlay';
import { CoverageCredits } from '../CoverageCredits';
import { CoverageHoverTooltip } from '../CoverageHoverTooltip';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectCreation } from '../../contexts/ProjectCreationContext';
import { useDrawingContext } from './DrawingContext';
import { GeometryManager } from './GeometryManager';
import { AddressSearchPanel } from './AddressSearchPanel';
import { DrawingControls } from './DrawingControls';
import { PricingDisplay } from './PricingDisplay';
import { DrawingBasemapControl } from './DrawingBasemapControl';
import { BufferConfig } from './BufferConfig';
import { useEventSubscribe, eventBus } from '../../events';
import { usePolygonDrawing } from '../../hooks/usePolygonDrawing';
import { useUserProjectsContext } from '../../contexts/UserProjectsContext';
import { isLiveDemo } from '../../utils/capabilities';
import { useBilling } from '../../contexts/BillingContext';
import { hasUnlimitedPlan } from '../../utils/entitlements';
import { projectService } from '../../services/projectService';
import { clampPointToPolygon, ringToPolygonGeoJSON, parsePolygonRing } from '../../utils/geometryUtils';
import { getDemAoiMode, setDemAoiMode } from '../../utils/demAoiMode';
export const DrawingOrchestrator = React.memo(({ viewer, projectName, projectDescription, initialCoordinates, initialBufferDistance, onGeometryChange, isAccountModalOpen = false, isLoginModalOpen = false, currentBasemap, onChangeBasemap, onFinish, onCancel, onShowAccountModal, onShowCreateProjectModal }) => {
    const { isAuthenticated } = useAuth();
    const { storePendingProject } = useProjectCreation();
    const { state, actions, computed } = useDrawingContext();
    const { projects } = useUserProjectsContext();
    // An active unlimited plan creates unfettered — the one-active-demo gate
    // below never applies. The plan is resolved server-side from the account's
    // grants, the same signal the access gate folds.
    const { entitlements } = useBilling();
    const unlimited = hasUnlimitedPlan(entitlements);
    // === Drawing core (unified with polygon annotation tool) ====================
    // Single source of truth for vertex/polygon entity management + map events.
    // The DrawingContext keeps everything ELSE (address, area, UI, buffer,
    // contour config); a bridge effect below mirrors the hook's vertex list
    // into the context so the existing area/pricing/buffer/billing flow keeps
    // working unchanged.
    //
    // onFinishRequested = double-click → opens buffer config (same flow as the
    // Finish Drawing button).
    const handleFinishRef = useRef(() => { });
    // Colour scheme matches the polygon annotation tool (DEFAULT_POLYGON_COLOR
    // = '#DC143C' crimson) so both flows look identical during active drawing.
    // Active/selected vertex stays yellow (hook-hardcoded), giving a clean
    // crimson→yellow swap.
    // DEM area-of-interest: confine drawing to the uploaded DEM's coverage. Read
    // the mode set just before drawing started (stable for this drawing session).
    // Data-coverage overlay on the area-selection screen. The on/off toggle lives
    // in the address/search bar (always discoverable); state is owned here so the
    // overlay survives that panel hiding. Default ON for property drawing so people
    // see where data is available; OFF for a DEM area-of-interest (own terrain is
    // already loaded, so public coverage is irrelevant and the search bar is hidden).
    const [coverageVisible, setCoverageVisible] = useState(() => !getDemAoiMode());
    const [coverageOpacity, setCoverageOpacity] = useState(0.2);
    const [coverageHover, setCoverageHover] = useState(null);
    // Live side-measurement labels while delineating the boundary. Default OFF —
    // delineation is about the total area, not edge lengths — with its own
    // preference (separate from the markup tool's). Length unit follows the
    // Acres/Hectares toggle (hectares → metric metres).
    const [showLabels, setShowLabels] = useState(() => localStorage.getItem('landscope_delineation_show_labels') === 'true');
    const toggleShowLabels = useCallback(() => {
        setShowLabels((prev) => {
            const next = !prev;
            try {
                localStorage.setItem('landscope_delineation_show_labels', next ? 'true' : 'false');
            }
            catch ( /* ignore */_a) { /* ignore */ }
            return next;
        });
    }, []);
    // Edge-label length unit. Independently selectable from the draw panel
    // (feet/metres/yards/kilometres/miles) once labels are shown; persisted on
    // its own key. First-run default follows the Acres/Hectares toggle so metric
    // users start in metres without an extra click.
    const [labelUnit, setLabelUnit] = useState(() => {
        const stored = localStorage.getItem('landscope_delineation_label_unit');
        if (stored === 'm' || stored === 'km' || stored === 'ft' || stored === 'yd' || stored === 'mi')
            return stored;
        return state.ui.areaUnit === 'hectares' ? 'm' : 'ft';
    });
    const changeLabelUnit = useCallback((unit) => {
        setLabelUnit(unit);
        try {
            localStorage.setItem('landscope_delineation_label_unit', unit);
        }
        catch ( /* ignore */_a) { /* ignore */ }
    }, []);
    useDataCoverageOverlay(viewer, {
        visible: coverageVisible,
        opacity: coverageOpacity,
        onHover: setCoverageHover,
    });
    const demAoiForDraw = getDemAoiMode();
    const demAoiRing = useMemo(() => {
        if (!demAoiForDraw)
            return null;
        return parsePolygonRing(demAoiForDraw.demBoundsGeojson);
    }, [demAoiForDraw === null || demAoiForDraw === void 0 ? void 0 : demAoiForDraw.demBoundsGeojson]);
    const transformPoint = useMemo(() => {
        if (!demAoiRing)
            return undefined;
        return (position) => {
            const carto = Cesium.Cartographic.fromCartesian(position);
            const pt = { lat: Cesium.Math.toDegrees(carto.latitude), lng: Cesium.Math.toDegrees(carto.longitude) };
            const clamped = clampPointToPolygon(pt, demAoiRing);
            if (clamped.lat === pt.lat && clamped.lng === pt.lng)
                return position;
            return Cesium.Cartesian3.fromDegrees(clamped.lng, clamped.lat);
        };
    }, [demAoiRing]);
    const drawing = usePolygonDrawing({
        viewer,
        isActive: true,
        showLabels,
        lengthUnit: labelUnit,
        onFinishRequested: () => { handleFinishRef.current(); },
        pointColor: Cesium.Color.fromCssColorString('#DC143C'),
        pointOutlineColor: Cesium.Color.BLACK,
        polygonFillColor: Cesium.Color.fromCssColorString('#DC143C').withAlpha(0.3),
        polygonOutlineColor: Cesium.Color.fromCssColorString('#DC143C'),
        transformPoint,
    });
    // DEM AOI: fly to the uploaded DEM's extent and draw its coverage outline so
    // the user can see (and stay within) the data boundary while delineating.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !demAoiRing || demAoiRing.length < 3)
            return;
        const lons = demAoiRing.map((c) => c.lng);
        const lats = demAoiRing.map((c) => c.lat);
        viewer.camera.flyTo({
            destination: Cesium.Rectangle.fromDegrees(Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)),
            duration: 1.2,
        });
        const positions = demAoiRing.map((c) => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
        const outline = viewer.entities.add({
            polyline: {
                positions: [...positions, positions[0]],
                width: 2,
                material: Cesium.Color.fromCssColorString('#38BDF8'),
                clampToGround: true,
            },
        });
        return () => {
            if (viewer && !viewer.isDestroyed())
                viewer.entities.remove(outline);
        };
    }, [viewer, demAoiRing]);
    // Bridge: mirror the hook's vertex list into DrawingContext so downstream
    // consumers (area calculation, pricing display, buffer, billing gate,
    // onFinish handoff) keep reading from the same place they always did.
    useEffect(() => {
        actions.setPoints(drawing.cartesianPoints, []);
    }, [drawing.cartesianPoints, actions]);
    // Initial coordinates: when the caller pre-populates (project recreation
    // or upload flow), push them into the hook so it creates the entities.
    // The hook's setPoints accepts {lat, lng} objects directly.
    useEffect(() => {
        if (!viewer || !initialCoordinates || initialCoordinates.length < 3)
            return;
        if (drawing.points.length === 0) {
            drawing.setPoints(initialCoordinates);
        }
        // Disabling exhaustive-deps: we want this to fire only when the prop
        // arrives, not whenever drawing.points changes (which would re-enter
        // the branch and never stabilise).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewer, initialCoordinates]);
    // Upload pre-population (window.__initialUploadCoordinates) — same
    // mechanism the old GeometryManager used, but now routes through the
    // hook so entities get created consistently.
    useEffect(() => {
        if (!viewer)
            return;
        const globalAny = window;
        const pending = globalAny.__initialUploadCoordinates;
        if (pending && Array.isArray(pending) && pending.length >= 3 && drawing.points.length === 0) {
            globalAny.__initialUploadCoordinates = undefined;
            const coords = pending.filter(pt => Number.isFinite(pt.lat) && Number.isFinite(pt.lng));
            if (coords.length >= 3) {
                drawing.setPoints(coords);
                const avgLat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
                const avgLng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
                try {
                    viewer.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(avgLng, avgLat, 5000), duration: 1.2 });
                }
                catch (_a) { }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewer, drawing.points.length]);
    // Set initial buffer distance if provided
    useEffect(() => {
        if (initialBufferDistance !== undefined && initialBufferDistance > 0) {
            actions.setBufferDistance(initialBufferDistance);
        }
    }, [initialBufferDistance, actions]);
    // Emit geometry changes if requested
    useEffect(() => {
        if (onGeometryChange) {
            onGeometryChange(computed.coordinates);
        }
    }, [computed.coordinates, onGeometryChange]);
    // Destructure state for easier access
    const { showOrderModal, showBufferConfig, standardContourUnit, customContours } = state.ui;
    const { data: areaData } = state.area;
    // Track if we're waiting for contour config from modal
    const [waitingForContours, setWaitingForContours] = React.useState(false);
    // Determine if any modal is open that should hide drawing tools
    const isAnyModalOpen = showOrderModal || showBufferConfig || isAccountModalOpen || isLoginModalOpen || waitingForContours;
    // "Hard" modals where even the data-coverage control should be hidden
    // (checkout / login / account / processing). The buffer/water configuration
    // stage is deliberately excluded so coverage stays available there — the
    // address panel just compresses to the coverage control.
    const hardModalOpen = showOrderModal || isAccountModalOpen || isLoginModalOpen || waitingForContours;
    // Cleanup entities when component unmounts or drawing is cancelled
    const cleanupEntities = useCallback(() => {
        // Clear the in-progress Cesium entities owned by usePolygonDrawing
        // (vertices, polygon fill, outline polyline) before resetting the
        // React-level DrawingContext state. Without this the drawing-mode
        // entities persist in the 'polygon-drawing' CustomDataSource and
        // look like a phantom polygon that the Project Boundary visibility
        // toggle cannot control (it owns a different render path).
        drawing.clearAll();
        actions.resetState();
    }, [actions, drawing]);
    // Cancel: clear the in-progress drawing entities before delegating to the
    // parent. Without cleanupEntities() the red polygon is orphaned in the
    // 'polygon-drawing' data source and can't be removed via the normal
    // Project Boundary visibility toggle.
    const handleCancel = useCallback(() => {
        setDemAoiMode(null);
        cleanupEntities();
        onCancel();
    }, [cleanupEntities, onCancel]);
    // Keyboard shortcuts during boundary drawing — mirror the polygon
    // annotation tool so users build muscle memory across both flows:
    //   Esc            close modal, then buffer config, then cancel drawing
    //   Backspace/Del  delete selected vertex, or (if none) the last placed
    //   Enter          finish drawing (>= 3 vertices), opens buffer config
    // Backspace/Enter are suppressed when focus is in a text input so
    // editing fields (project name / address search) don't blow away points.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (showOrderModal) {
                    actions.showOrderModal(false);
                }
                else if (showBufferConfig) {
                    actions.showBufferConfig(false);
                }
                else if (!waitingForContours) {
                    handleCancel();
                }
                return;
            }
            // Block other shortcuts when a modal/overlay is layered on top.
            if (showOrderModal || showBufferConfig || waitingForContours)
                return;
            const target = event.target;
            const inEditable = !!target && (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable);
            if (inEditable)
                return;
            // Source of truth is now the drawing hook, not DrawingContext.
            const pointsLen = drawing.points.length;
            const selectedIdx = drawing.selectedVertexIndex;
            if ((event.key === 'Backspace' || event.key === 'Delete') && pointsLen > 0) {
                event.preventDefault();
                // Route through the drawing hook so the actual on-map entity is
                // removed too — dispatching directly to the reducer only touches
                // the JS state and was the source of "node still on screen" bugs.
                if (selectedIdx >= 0 && selectedIdx < pointsLen) {
                    drawing.deleteSelectedVertex();
                }
                else {
                    drawing.removeLastPoint();
                }
                return;
            }
            if (event.key === 'Enter' && computed.hasMinimumPoints) {
                event.preventDefault();
                actions.showBufferConfig(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        showOrderModal,
        showBufferConfig,
        waitingForContours,
        handleCancel,
        actions,
        drawing.points.length,
        drawing.selectedVertexIndex,
        drawing.deleteSelectedVertex,
        drawing.removeLastPoint,
        computed.hasMinimumPoints,
    ]);
    // Handle finish button click - shows buffer config
    const handleFinish = useCallback(() => {
        if (!computed.hasMinimumPoints)
            return;
        actions.showBufferConfig(true);
    }, [computed.hasMinimumPoints, actions]);
    // Keep handleFinishRef in sync so the drawing hook's double-click trigger
    // (onFinishRequested) lands on the latest closure.
    useEffect(() => {
        handleFinishRef.current = handleFinish;
    }, [handleFinish]);
    // Stash the current project data for creation after billing is resolved.
    // Contours can be passed in explicitly (contourOverride): the contour config
    // arrives via a 'project:contourConfigComplete' event whose payload is the
    // source of truth. Reading them back off DrawingContext here instead would
    // race the just-dispatched setStandardContourUnit/addCustomContour reducer
    // updates (not yet committed in the same tick), stashing an empty list and
    // silently dropping every custom contour from the created project.
    const stashProjectData = useCallback((contourOverride) => {
        var _a, _b, _c, _d, _e, _f;
        const unit = (_a = contourOverride === null || contourOverride === void 0 ? void 0 : contourOverride.standardContourUnit) !== null && _a !== void 0 ? _a : standardContourUnit;
        const customs = (_b = contourOverride === null || contourOverride === void 0 ? void 0 : contourOverride.customContours) !== null && _b !== void 0 ? _b : customContours;
        storePendingProject({
            projectName,
            projectDescription,
            coordinates: computed.coordinates,
            bufferDistanceMeters: state.ui.bufferDistanceMeters || 0,
            areaUnit: 'acres',
            projectAreaAcres: ((_d = (_c = state.area.data) === null || _c === void 0 ? void 0 : _c.projectAreaAcres) !== null && _d !== void 0 ? _d : 0) + ((_f = (_e = state.area.data) === null || _e === void 0 ? void 0 : _e.bufferAreaAcres) !== null && _f !== void 0 ? _f : 0),
            standardContourUnit: unit,
            customContours: customs === null || customs === void 0 ? void 0 : customs.map(c => ({
                unit: c.unit,
                intervalValue: c.intervalValue,
            })),
        });
    }, [projectName, projectDescription, computed.coordinates, state.ui.bufferDistanceMeters, state.area.data, standardContourUnit, customContours, storePendingProject]);
    // End of the draw: hand the finished AOI to the create confirmation, which
    // owns the create RPC. Every authenticated user can create a project — the
    // backend bootstraps it as an unpaid 7-day demo, no purchase required.
    const handleDrawComplete = useCallback((contourOverride) => {
        stashProjectData(contourOverride);
        actions.showOrderModal(false);
        cleanupEntities();
        onFinish(projectName, projectDescription, computed.coordinates);
        // One-active-demo gate (UX layer; the backend enforces regardless). If the
        // user already has an active high-res demo, offer to unlock it first
        // instead of silently creating this one locked. Owned projects only: a
        // demo someone shared with you never occupies your slot. An unlimited plan
        // skips the gate entirely — its grant covers every project in the account.
        const nowSec = Date.now() / 1000;
        const activeDemo = unlimited ? undefined : projects.find((p) => p.isOwner !== false &&
            isLiveDemo(p.accessLevel) &&
            p.demoExpiresAt &&
            nowSec < p.demoExpiresAt);
        if (activeDemo) {
            eventBus.emit('demo:promptUnlockBeforeCreate', {
                activeDemoId: activeDemo.id,
                activeDemoName: activeDemo.name,
                activeDemoExpiresAt: activeDemo.demoExpiresAt,
                pendingProjectName: projectName,
            });
            return;
        }
        eventBus.emit('project:confirmCreate', { projectName });
    }, [stashProjectData, actions, cleanupEntities, onFinish, projectName, projectDescription, computed.coordinates, projects, unlimited]);
    // Listen for contour config complete event from CreateProjectModal.
    // The payload is the source of truth for the contour config; pass it
    // straight into the billing gate so it lands in the stashed pending
    // project. We still sync DrawingContext for any other consumers, but
    // must NOT rely on those reducer updates being committed before the
    // gate reads them in this same tick.
    useEventSubscribe('project:contourConfigComplete', (payload) => {
        actions.setStandardContourUnit(payload.standardContourUnit);
        actions.clearCustomContours();
        payload.customContours.forEach(contour => actions.addCustomContour(contour));
        setWaitingForContours(false);
        handleDrawComplete({
            standardContourUnit: payload.standardContourUnit,
            customContours: payload.customContours,
        });
    }, [actions, handleDrawComplete]);
    // Listen for return to buffer config event from CreateProjectModal
    useEventSubscribe('project:returnToBufferConfig', () => {
        setWaitingForContours(false);
        actions.showBufferConfig(true);
    }, [actions]);
    // Handle buffer config continue - dispatch event to show modal contour step
    const handleBufferContinue = useCallback(() => {
        // DEM area-of-interest: apply the drawn polygon as the AOI on the existing
        // uploaded DEM project, then route to the DEM contour step. The drawn ring
        // (computed.coordinates) becomes the property; buildDemAoiBounds derives the
        // buffer (extended) clamped to the DEM's data extent.
        const demAoi = getDemAoiMode();
        if (demAoi) {
            actions.showBufferConfig(false);
            // The modal wiped its form state when it closed for drawing, so every
            // restore path must carry name/description (and project id/bounds) back to
            // it — otherwise the contour step submits an empty description.
            const finish = (event, payload) => {
                setDemAoiMode(null);
                cleanupEntities();
                onFinish(projectName, projectDescription, computed.coordinates);
                eventBus.emit(event, payload);
                if (onShowCreateProjectModal)
                    onShowCreateProjectModal();
            };
            const fail = (message) => finish('dem:aoiError', {
                message,
                projectId: demAoi.projectId,
                demBoundsGeojson: demAoi.demBoundsGeojson,
                projectName,
                projectDescription,
            });
            if (computed.coordinates.length < 3) {
                fail('Draw at least three points to define an area.');
                return;
            }
            // Property = the drawn area; extended (water flow) = the buffer already
            // clamped to the DEM coverage in DrawingContext, so it never exceeds the
            // uploaded boundary. Fall back to the AOI itself when no buffer was set.
            const bufferRing = computed.bufferedCoordinates.length >= 3 ? computed.bufferedCoordinates : computed.coordinates;
            const bounds = {
                requestedBoundsGeoJSON: ringToPolygonGeoJSON(computed.coordinates),
                bufferBoundsGeoJSON: ringToPolygonGeoJSON(bufferRing),
            };
            projectService.finalizeDEMUpload(demAoi.projectId, bounds)
                .then((fin) => {
                if (!fin.success) {
                    fail(fin.message || 'Failed to apply the area of interest.');
                    return;
                }
                finish('dem:aoiConfigured', {
                    projectId: demAoi.projectId,
                    requestedBoundsGeojson: fin.requestedBoundsGeojson,
                    requestedAreaAcres: fin.requestedAreaAcres,
                    projectName,
                    projectDescription,
                });
            })
                .catch((err) => fail((err === null || err === void 0 ? void 0 : err.message) || 'Failed to apply the area of interest.'));
            return;
        }
        actions.showBufferConfig(false);
        setWaitingForContours(true);
        // Dispatch event to reopen CreateProjectModal at contours step
        eventBus.emit('project:bufferConfigComplete', {
            projectName,
            projectDescription,
            standardContourUnit
        });
        // Also trigger the modal to open
        if (onShowCreateProjectModal) {
            onShowCreateProjectModal();
        }
    }, [actions, projectName, projectDescription, standardContourUnit, onShowCreateProjectModal, cleanupEntities, onFinish, computed.coordinates, computed.bufferedCoordinates]);
    // Handle buffer config cancel - reset buffer to provide consistent draw -> buffer experience
    const handleBufferCancel = useCallback(() => {
        actions.showBufferConfig(false);
        actions.setBufferDistance(100);
    }, [actions]);
    // Handle order cancel - return to contour config in modal
    const handleOrderCancel = useCallback(() => {
        actions.showOrderModal(false);
        setWaitingForContours(true);
        // Dispatch event to reopen modal at contours step
        eventBus.emit('project:bufferConfigComplete', {
            projectName,
            projectDescription,
            standardContourUnit
        });
        if (onShowCreateProjectModal) {
            onShowCreateProjectModal();
        }
    }, [actions, projectName, projectDescription, standardContourUnit, onShowCreateProjectModal]);
    // Handle checkout close - exits drawing mode completely
    const handleCheckoutClose = useCallback(() => {
        actions.showOrderModal(false);
        cleanupEntities();
        onCancel();
    }, [actions, cleanupEntities, onCancel]);
    return (_jsxs(_Fragment, { children: [_jsx(DrawingBasemapControl, { viewer: viewer, currentBasemap: currentBasemap, onChangeBasemap: onChangeBasemap }), coverageVisible && !hardModalOpen && (_jsx("div", { className: "fixed bottom-7 left-2 z-30 max-w-[min(90vw,30rem)]", children: _jsx("div", { className: "rounded-lg px-3 py-1 shadow-lg border", style: { backgroundColor: 'rgba(24,24,24,0.85)', borderColor: 'var(--pewter)' }, children: _jsx(CoverageCredits, { className: "text-[9px] leading-snug" }) }) })), _jsx(CoverageHoverTooltip, { hover: coverageHover }), !isAnyModalOpen && (_jsx("button", { onClick: handleCancel, className: "fixed top-4 right-4 z-50 p-2 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg transition-colors", title: "Cancel drawing (ESC)", children: _jsx(X, { size: 24 }) })), _jsx(GeometryManager, { viewer: viewer, isActive: true, polygonEntityRef: drawing.polygonEntityRef }), !demAoiForDraw && (_jsx(AddressSearchPanel, { viewer: viewer, 
                // Full panel while drawing; compresses to just the coverage control
                // during the buffer/water stage; fully hidden for checkout/login.
                isVisible: !hardModalOpen, compact: showBufferConfig, coverageVisible: coverageVisible, onToggleCoverage: () => setCoverageVisible((v) => !v), coverageOpacity: coverageOpacity, onCoverageOpacityChange: setCoverageOpacity })), _jsx(DrawingControls, { isVisible: !isAnyModalOpen, onFinish: handleFinish, selectedVertexIndex: drawing.selectedVertexIndex, onDeleteSelectedVertex: drawing.deleteSelectedVertex, showLabels: showLabels, onToggleLabels: toggleShowLabels, labelUnit: labelUnit, onChangeLabelUnit: changeLabelUnit }), _jsx(PricingDisplay, { isVisible: !isAnyModalOpen && computed.hasMinimumPoints }), _jsx(BufferConfig, { isVisible: showBufferConfig, onContinue: handleBufferContinue, onCancel: handleBufferCancel }), waitingForContours && (_jsx("div", { className: "fixed bottom-4 left-1/2 transform -translate-x-1/2 z-40", children: _jsx("div", { className: "bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg text-sm", children: "Configure contours in the wizard..." }) }))] }));
});
DrawingOrchestrator.displayName = 'DrawingOrchestrator';
